import type {
  ChannelCommand,
  CommandValue,
  RailEntry,
  Webhook,
  BotApplication,
  Ban,
  ServerEmoji,
  ServerSound,
  Category,
  Channel,
  ChannelType,
  DirectChannel,
  FileMeta,
  Invite,
  Member,
  InboxItem,
  ReminderItem,
  WelcomeScreen,
  FollowedThread,
  LinkPreview,
  Message,
  MessageAuthor,
  NotifyLevel,
  Override,
  PublicUser,
  Role,
  SelfUser,
  ServerState,
  ServerSummary,
  Session,
  Thread,
  ThreadListItem,
  Tokens,
  Timestamp,
  Uuid,
  VoiceGrant,
  Ingress,
  VoiceParticipant,
  CallRing,
  AuditEntry,
  ForumPost,
  ForumTag,
  ServerEvent,
  ServerEventDraft,
  ServerSticker,
  Poll,
  PollDraft,
  ThreadReplyExtra,
} from "./types";
import { holdRestartBlocker } from "@shared/lib/desktop";

/**
 * Адрес API.
 *
 * В вебе он относительный: страница и API на одном происхождении, а в
 * разработке запросы идут через прокси Vite. В собранном приложении Tauri
 * страница живёт по своей схеме, и относительный путь вёл бы в никуда —
 * поэтому там адрес задаётся при сборке через VITE_API_ORIGIN.
 */
export const ORIGIN = (import.meta.env.VITE_API_ORIGIN ?? "").replace(/\/$/, "");

/**
 * Адрес узла вебсокета.
 *
 * Отдельно от API намеренно: это разные службы и в разработке они слушают
 * разные порты. В вебе оба скрыты за прокси и адрес не нужен, а в собранном
 * приложении один общий адрес увёл бы вебсокет на порт API.
 */
export const REALTIME_ORIGIN = (
  import.meta.env.VITE_WS_ORIGIN ??
  import.meta.env.VITE_API_ORIGIN ??
  ""
).replace(/\/$/, "");

const BASE = `${ORIGIN}/api`;

/** Фильтры поиска — как «Фильтры» в Discord. Списки — «любой из выбранных». */
export interface SearchFilter {
  /** «от:» — от любого из этих людей. */
  authorIds?: Uuid[];
  /** «в:» — только в этих каналах сервера. */
  channelIds?: Uuid[];
  /** «с упоминанием:» — зовёт любого из этих людей. */
  mentionIds?: Uuid[];
  has?: "link" | "file" | "image" | null;
  /** «после:» — написанное с этого времени. */
  after?: Timestamp | null;
  /** «до:» — написанное раньше этого времени. */
  before?: Timestamp | null;
  /** «закреплено»: только закреплённое (`true`) или только нет (`false`). */
  pinned?: boolean | null;
  /** «тип автора»: люди или боты. */
  authorType?: "user" | "bot" | null;
  /** Порядок находок, как переключатель в Discord; по умолчанию — новые сверху. */
  sort?: "newest" | "oldest" | "relevant";
  /** Только в этой ветке — «Поиск» из меню ветки. */
  threadId?: Uuid | null;
}

function searchParams(q: string, filter: SearchFilter): string {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  for (const id of filter.authorIds ?? []) params.append("author_id", id);
  for (const id of filter.channelIds ?? []) params.append("channel_id", id);
  for (const id of filter.mentionIds ?? []) params.append("mentions", id);
  if (filter.has) params.set("has", filter.has);
  if (filter.after) params.set("after", filter.after);
  if (filter.before) params.set("before", filter.before);
  if (filter.pinned !== null && filter.pinned !== undefined) params.set("pinned", String(filter.pinned));
  if (filter.authorType) params.set("author_type", filter.authorType);
  if (filter.sort && filter.sort !== "newest") params.set("sort", filter.sort);
  if (filter.threadId) params.set("thread_id", filter.threadId);
  return params.toString();
}

/**
 * Отказ от сервера в разобранном виде.
 *
 * `code` и `until` — то, ради чего это отдельный класс: экран входа
 * показывает не «что-то пошло не так», а настоящую причину и срок.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly until: Date | null;

  constructor(status: number, message: string, code: string | null, until: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.until = until ? new Date(until) : null;
  }
}

interface Store {
  access: string | null;
  refresh: string | null;
}

const KEY = "airel.tokens";

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { access: null, refresh: null };
    const parsed = JSON.parse(raw) as Partial<Store>;
    return { access: parsed.access ?? null, refresh: parsed.refresh ?? null };
  } catch {
    return { access: null, refresh: null };
  }
}

let tokens: Store = read();
let onSignedOut: (() => void) | null = null;

/**
 * Id своего соединения с узлом realtime — из его первого события `ready`.
 *
 * Уходит с каждым запросом в `x-connection-id`: API исключает это соединение
 * из рассылки, и вкладка не получает эхом то, что уже пришло ей в ответе.
 * Остальные вкладки и устройства событие получают. `null` — соединения нет,
 * и исключать некого.
 */
let connectionId: string | null = null;

export function setConnectionId(id: string | null) {
  connectionId = id;
}

export function setSignOutHandler(handler: () => void) {
  onSignedOut = handler;
}

export function currentTokens(): Store {
  return tokens;
}

export function storeTokens(next: Tokens | null) {
  tokens = next ? { access: next.access_token, refresh: next.refresh_token } : { access: null, refresh: null };

  if (next) {
    localStorage.setItem(KEY, JSON.stringify(tokens));
  } else {
    localStorage.removeItem(KEY);
  }
}

/**
 * Обновление доступа выполняется один раз на все запросы, которые в него
 * упёрлись: иначе десяток параллельных запросов после сна ноутбука пришлёт
 * десяток обновлений, и все, кроме первого, получат отказ по уже
 * использованному refresh-токену.
 */
let refreshing: Promise<boolean> | null = null;
let protectedRequestSequence = 0;

function holdApiOperation(title: string, detail: string): () => void {
  protectedRequestSequence += 1;
  return holdRestartBlocker({
    id: `api-request:${protectedRequestSequence}`,
    title,
    detail,
    kind: "wait",
  });
}

async function refreshAccessOnce(): Promise<boolean> {
  const releaseRestart = holdApiOperation(
    "Продлевается сеанс",
    "дождитесь сохранения нового ключа доступа",
  );
  const used = tokens.refresh!;

  try {
    const response = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refresh_token: used }),
    });

    if (response.ok) {
      storeTokens((await response.json()) as Tokens);
      return true;
    }

    /*
     * Внутри вкладки обновление одно, но вкладок бывает несколько, и хранилище
     * у них общее. Соседка могла обновиться первой — тогда наш токен уже
     * потрачен, а в хранилище лежит свежий. Перечитываем: без этого одна из
     * двух открытых вкладок выкидывала бы на вход на ровном месте.
     */
    const stored = read();
    if (stored.refresh && stored.refresh !== used) {
      tokens = stored;
      return true;
    }

    return false;
  } catch (error) {
    // RestartInProgressError намеренно идёт вызывающему коду: считать его
    // сетевым отказом значило бы удалить ещё действующую сессию.
    if (error instanceof Error && error.name === "RestartInProgressError") throw error;
    return false;
  } finally {
    releaseRestart();
  }
}

async function refreshAccess(): Promise<boolean> {
  if (!tokens.refresh) return false;
  const flight = (refreshing ??= refreshAccessOnce());
  try {
    return await flight;
  } finally {
    if (refreshing === flight) refreshing = null;
  }
}

interface CallOptions {
  method?: string;
  body?: unknown;
  form?: FormData;
  /** Запросы без авторизации: вход и регистрация. */
  anonymous?: boolean;
  /** Повторная попытка после обновления доступа. Внутреннее. */
  retried?: boolean;
  /**
   * Пережить закрытие страницы.
   *
   * Обычный запрос браузер обрывает вместе со вкладкой, а этот доводит до
   * конца. Нужно ровно одному месту — выходу из звонка при уходе со
   * страницы, — и годится только для мелких тел: у `keepalive` свой предел
   * в 64 КиБ на всё.
   */
  keepalive?: boolean;
  /** Позволяет ограничить служебную сетевую уборку при закрытии/обновлении. */
  signal?: AbortSignal;
  /** Уборка перед выходом не должна запускать отдельную ротацию сессии. */
  refreshOnUnauthorized?: boolean;
}

async function call<T>(path: string, options: CallOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const releaseRestart =
    method === "GET" || method === "HEAD"
      ? null
      : holdApiOperation("Сохраняются изменения", "дождитесь ответа сервера");
  try {
    return await callRequest<T>(path, options);
  } finally {
    releaseRestart?.();
  }
}

async function callRequest<T>(path: string, options: CallOptions = {}): Promise<T> {
  const {
    method = "GET",
    body,
    form,
    anonymous,
    retried,
    keepalive,
    signal,
    refreshOnUnauthorized = true,
  } = options;
  const headers: Record<string, string> = {};

  if (!anonymous && tokens.access) headers.authorization = `Bearer ${tokens.access}`;
  if (connectionId) headers["x-connection-id"] = connectionId;
  if (body !== undefined) headers["content-type"] = "application/json";

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    keepalive,
    signal,
    body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });

  if (response.status === 401 && !anonymous && !retried && refreshOnUnauthorized) {
    if (await refreshAccess()) {
      return call<T>(path, { ...options, retried: true });
    }

    // Обновиться не вышло — сессия кончилась по-настоящему.
    storeTokens(null);
    onSignedOut?.();
  }

  if (!response.ok) {
    let message = response.statusText;
    let code: string | null = null;
    let until: string | null = null;

    try {
      const problem = (await response.json()) as {
        message?: string;
        error?: string;
        code?: string;
        until?: string;
      };
      message = problem.message ?? problem.error ?? message;
      code = problem.code ?? null;
      until = problem.until ?? null;
    } catch {
      /* тело не разобралось — останется статус */
    }

    throw new ApiError(response.status, message, code, until);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Отправка файла с сообщением о ходе.
 *
 * Через `XMLHttpRequest`, а не `fetch`, и это не вкусовщина: **`fetch` о ходе
 * отправки не сообщает вовсе.** У него есть событие только на приём ответа, а
 * отправку он показывает лишь потоковым телом (`duplex: "half"`), которое
 * требует HTTP/2 и поддержано не везде. Нам же один и тот же путь нужен и в
 * браузере, и в приложении.
 *
 * Отсюда повтор обвязки `call`: заголовок, разбор отказа и обновление токена
 * здесь свои. Два описания одного — плата за полоску, и она честнее, чем
 * перевод всего клиента на XHR ради одного запроса.
 */
function upload<T>(
  path: string,
  form: FormData,
  onProgress: ((sent: number, total: number) => void) | undefined,
  retried = false,
): Promise<T> {
  const releaseRestart = holdApiOperation("Загружается файл", "дождитесь ответа сервера");
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open("POST", `${BASE}${path}`);
    if (tokens.access) request.setRequestHeader("authorization", `Bearer ${tokens.access}`);
    if (connectionId) request.setRequestHeader("x-connection-id", connectionId);

    /*
     * `lengthComputable` бывает ложным — тогда доли нет, и звать с выдуманным
     * числом хуже, чем промолчать: полоска дёрнулась бы в никуда.
     */
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    });

    request.addEventListener("load", () => {
      const status = request.status;

      if (status === 401 && !retried) {
        void refreshAccess().then((fresh) => {
          if (fresh) {
            upload<T>(path, form, onProgress, true).then(resolve, reject);
            return;
          }

          storeTokens(null);
          onSignedOut?.();
          reject(new ApiError(401, "Сессия кончилась", null, null));
        });

        return;
      }

      if (status < 200 || status >= 300) {
        let message = request.statusText;
        let code: string | null = null;

        try {
          const problem = JSON.parse(request.responseText) as {
            message?: string;
            error?: string;
            code?: string;
          };
          message = problem.message ?? problem.error ?? message;
          code = problem.code ?? null;
        } catch {
          /* тело не разобралось — останется статус */
        }

        reject(new ApiError(status, message, code, null));
        return;
      }

      resolve((request.responseText ? JSON.parse(request.responseText) : undefined) as T);
    });

    // Сеть отвалилась: у XHR это отдельное событие, а не отказ по статусу.
    request.addEventListener("error", () =>
      reject(new ApiError(0, "Сеть не отвечает", null, null)),
    );

    request.send(form);
  }).finally(releaseRestart);
}

/** Адрес файла. Скачиванием или для показа — решает сервер, клиент лишь просит. */
/** Адрес вебхука — его отдают внешней программе. Пустой ORIGIN — тот же адрес, что у страницы. */
export function webhookUrl(id: Uuid, token: string): string {
  return `${ORIGIN || window.location.origin}/api/webhooks/${id}/${token}`;
}

export function fileUrl(id: Uuid): string {
  return `${BASE}/files/${id}`;
}

/**
 * Картинка карточки ссылки — через наш сервер, а не с чужого сайта. Тоже по токену.
 * У вставки бота — ещё и сообщение: сервер отдаёт только картинки из его вставок.
 */
export function embedImageUrl(image: string, messageId?: Uuid): string {
  const message = messageId ? `&message_id=${messageId}` : "";
  return `${BASE}/embeds/image?url=${encodeURIComponent(image)}${message}`;
}

export const api = {
  /**
   * Приложения и боты — портал разработчика. Токен бота приходит только при
   * создании и при сбросе: сервер хранит лишь его хэш.
   */
  applications: {
    list: () => call<{ items: BotApplication[] }>("/applications"),
    create: (name: string) =>
      call<{ application: BotApplication; token: string }>("/applications", { method: "POST", body: { name } }),
    /** Адрес вызовов сервер сперва проверяет PING-ом; пустая строка — убрать адрес. */
    update: (
      id: Uuid,
      patch: { name?: string; description?: string; flags?: number; interactions_endpoint_url?: string },
    ) => call<BotApplication>(`/applications/${id}`, { method: "PATCH", body: patch }),
    resetToken: (id: Uuid) => call<{ token: string }>(`/applications/${id}/token`, { method: "POST" }),
  },
  /**
   * Команды ботов в канале: подсказка «/» и вызов. Ответ бота приходит позже —
   * сообщением или эфемерным, а «думает» и «ответил» — событием `interaction`.
   */
  commands: {
    list: (channelId: Uuid) => call<{ items: ChannelCommand[] }>(`/channels/${channelId}/commands`),
    invoke: (
      channelId: Uuid,
      commandId: Uuid,
      options: CommandValue[],
      threadId: Uuid | null = null,
      targetId: Uuid | null = null,
    ) =>
      call<{ id: Uuid; command_id: Uuid; name: string }>(`/channels/${channelId}/interactions`, {
        method: "POST",
        body: { command_id: commandId, options, thread_id: threadId, target_id: targetId },
      }),
    /** Варианты придут событием `interaction/update` с `state: "choices"`. */
    autocomplete: (channelId: Uuid, commandId: Uuid, options: CommandValue[], threadId: Uuid | null = null) =>
      call<{ id: Uuid; command_id: Uuid; name: string }>(`/channels/${channelId}/autocomplete`, {
        method: "POST",
        body: { command_id: commandId, options, thread_id: threadId },
      }),
    /** Нажатие кнопки или выбор в списке сообщения бота. */
    press: (channelId: Uuid, messageId: Uuid, customId: string, values: string[] = []) =>
      call<{ id: Uuid }>(`/channels/${channelId}/messages/${messageId}/components`, {
        method: "POST",
        body: { custom_id: customId, values },
      }),
    /** Ответ в окне бота. */
    submit: (interactionId: Uuid, fields: { custom_id: string; value?: string; values?: string[] }[]) =>
      call<{ id: Uuid }>(`/interactions/${interactionId}/modal`, { method: "POST", body: { fields } }),
  },
  /** Приглашение бота на сервер — страница по ссылке `#oauth2/authorize?client_id=…`. */
  oauth2: {
    preview: (clientId: string) =>
      call<BotApplication>(`/oauth2/authorize?client_id=${encodeURIComponent(clientId)}`),
    authorize: (clientId: string, serverId: Uuid, permissions: string) =>
      call<void>("/oauth2/authorize", {
        method: "POST",
        body: { client_id: clientId, server_id: serverId, permissions },
      }),
  },
  /** Вебхуки канала — по праву управлять вебхуками. */
  webhooks: {
    list: (channelId: Uuid) => call<{ items: Webhook[] }>(`/channels/${channelId}/webhooks`),
    create: (channelId: Uuid, name: string) =>
      call<Webhook>(`/channels/${channelId}/webhooks`, { method: "POST", body: { name } }),
    rename: (id: Uuid, name: string) => call<Webhook>(`/webhooks/${id}`, { method: "PATCH", body: { name } }),
    remove: (id: Uuid) => call<void>(`/webhooks/${id}`, { method: "DELETE" }),
    /** Подписаться на канал объявлений: в `targetId` заведётся вебхук, через который придут новости. */
    follow: (sourceId: Uuid, targetId: Uuid) =>
      call<Webhook>(`/channels/${sourceId}/followers`, { method: "POST", body: { channel_id: targetId } }),
  },
  auth: {
    login: (email: string, password: string) =>
      call<Tokens>("/auth/login", { method: "POST", body: { email, password }, anonymous: true }),

    register: (body: {
      username: string;
      email: string;
      password: string;
      display_name: string;
      /** Код приглашения. Нужен всем, кроме самого первого в базе. */
      invite?: string;
    }) =>
      call<{ id: Uuid; handle: string }>("/auth/register", {
        method: "POST",
        body,
        anonymous: true,
      }),

    connectionToken: () =>
      call<{ connection_token: string }>("/auth/connection-token", { method: "POST" }),
  },

  account: {
    sessions: () => call<{ items: Session[] }>("/account/sessions"),
    revokeOne: (id: Uuid) => call<void>(`/account/sessions/${id}`, { method: "DELETE" }),
    revokeOthers: () => call<{ sessions_revoked: number }>("/account/sessions", { method: "DELETE" }),
    logout: () => call<void>("/account/logout", { method: "POST" }),
    changePassword: (current_password: string, new_password: string) =>
      call<{ sessions_revoked: number }>("/account/password", {
        method: "PATCH",
        body: { current_password, new_password },
      }),
  },

  users: {
    /** Носить тег сервера рядом с именем; `null` — снять. */
    wearTag: (serverId: Uuid | null) =>
      call<{ server_id: Uuid | null; tag: string | null }>("/users/current/tag", {
        method: "PUT",
        body: { server_id: serverId },
      }),
    me: () => call<SelfUser>("/users/current"),
    /** «Входящие → Упоминания»: где назвали или ответили, свежие сверху. */
    mentions: () => call<InboxItem[]>("/users/current/mentions"),
    /** Сохранённые сообщения — свои закладки, последние сохранённые сверху. */
    saved: () => call<InboxItem[]>("/users/current/saved"),
    /** Сохранить; повтор без последствий, сверх сотни — `saved_full`. */
    save: (messageId: Uuid) => call<void>(`/users/current/saved/${messageId}`, { method: "PUT" }),
    unsave: (messageId: Uuid) => call<void>(`/users/current/saved/${messageId}`, { method: "DELETE" }),
    /** Свои напоминания о сообщениях, ближайшие сверху; сработавшие тоже. */
    reminders: () => call<ReminderItem[]>("/users/current/reminders"),
    /** Напомнить к сроку или перенести срок; `reminder_time` — срок вне пределов, `reminders_full` — мест нет. */
    remind: (messageId: Uuid, at: Date) =>
      call<void>(`/users/current/reminders/${messageId}`, {
        method: "PUT",
        body: { remind_at: at.toISOString() },
      }),
    forget: (messageId: Uuid) => call<void>(`/users/current/reminders/${messageId}`, { method: "DELETE" }),
    byId: (id: Uuid) => call<PublicUser>(`/users/${id}`),
    /** Общие серверы и общие друзья — только пересечение со своим. */
    mutual: (id: Uuid) => call<{ server_ids: Uuid[]; friend_ids: Uuid[] }>(`/users/${id}/mutual`),
    /** Своя заметка о человеке — как в Discord: видна только себе. `null` — её нет. */
    note: (id: Uuid) => call<{ note: string | null }>(`/users/${id}/note`),
    setNote: (id: Uuid, note: string | null) =>
      call<void>(`/users/${id}/note`, { method: "PUT", body: { note } }),
    /** Хэндл приходит одной строкой `alex#0042`, а маршрут разделён на две части. */
    byHandle: (handle: string) => {
      const [username, discriminator] = handle.split("#");
      return call<PublicUser>(
        `/users/by-handle/${encodeURIComponent(username)}/${encodeURIComponent(discriminator ?? "")}`,
      );
    },
    /** Ряд серверов: порядок и папки — общие для устройств. */
    rail: () => call<{ items: RailEntry[] }>("/users/current/rail"),
    saveRail: (items: RailEntry[]) =>
      call<{ items: RailEntry[] }>("/users/current/rail", { method: "PUT", body: { items } }),
    /** «Личные сообщения от участников серверов»: выключено — пишут только друзья. */
    setPrivacy: (allowDms: boolean) =>
      call<void>("/users/current/privacy", { method: "PUT", body: { allow_dms: allowDms } }),
    updateProfile: (body: Record<string, unknown>) =>
      call<SelfUser>("/users/current", { method: "PATCH", body }),
    setAvatar: (file_id: Uuid | null) =>
      call<SelfUser>("/users/current/avatar", { method: "PUT", body: { file_id } }),
    /** Картинка баннера; `null` — убрать, останется цвет. */
    setBanner: (file_id: Uuid | null) =>
      call<SelfUser>("/users/current/banner", { method: "PUT", body: { file_id } }),
    /** Украшение аватара; `null` — снять. */
    setDecoration: (file_id: Uuid | null) =>
      call<SelfUser>("/users/current/decoration", { method: "PUT", body: { file_id } }),
    updateUsername: (username: string, password: string) =>
      call<SelfUser>("/users/current/username", { method: "PATCH", body: { username, password } }),
  },

  relationships: {
    list: () => call<{ items: PublicUser[] }>("/users/current/relationships"),
    addByHandle: (handle: string) =>
      call<PublicUser>("/users/friend", { method: "POST", body: { handle } }),
    add: (id: Uuid) => call<PublicUser>(`/users/${id}/friend`, { method: "PUT" }),
    remove: (id: Uuid) => call<PublicUser>(`/users/${id}/friend`, { method: "DELETE" }),
    block: (id: Uuid) => call<PublicUser>(`/users/${id}/block`, { method: "PUT" }),
    unblock: (id: Uuid) => call<PublicUser>(`/users/${id}/block`, { method: "DELETE" }),
  },

  servers: {
    list: () => call<ServerSummary[]>("/servers"),
    state: (id: Uuid) => call<ServerState>(`/servers/${id}`),
    /** Сервер со стартовыми категориями и каналами — их ответ отдаёт сразу. */
    create: (name: string, description?: string) =>
      call<{ server: ServerSummary; categories: Category[]; channels: Channel[] }>("/servers", {
        method: "POST",
        body: { name, description: description ?? null },
      }),
    update: (id: Uuid, body: Record<string, unknown>) =>
      call<ServerSummary>(`/servers/${id}`, { method: "PATCH", body }),
    /** «Принимаю правила»: до этого новичок только читает. */
    acceptRules: (id: Uuid) => call<void>(`/servers/${id}/rules/accept`, { method: "POST" }),
    remove: (id: Uuid) => call<void>(`/servers/${id}`, { method: "DELETE" }),
    /** Передача владения. Только владельцу: это признак, а не право роли. */
    transfer: (id: Uuid, user_id: Uuid) =>
      call<ServerSummary>(`/servers/${id}/owner`, { method: "PUT", body: { user_id } }),
    leave: (id: Uuid) => call<void>(`/servers/${id}/users/current`, { method: "DELETE" }),
    kick: (id: Uuid, userId: Uuid) =>
      call<void>(`/servers/${id}/users/${userId}`, { method: "DELETE" }),
    /** Ник на сервере; `null` — снять. Свой — каждому, чужой — с правом и старшинством. */
    nickname: (id: Uuid, userId: Uuid, nickname: string | null) =>
      call<void>(`/servers/${id}/members/${userId}/nickname`, { method: "PUT", body: { nickname } }),
    /**
     * Заглушить или оглушить на сервере, как в Discord; не переданное не
     * трогается. Держится во всех голосовых каналах сервера, пока не снимут.
     */
    voice: (id: Uuid, userId: Uuid, change: { muted?: boolean; deafened?: boolean }) =>
      call<void>(`/servers/${id}/members/${userId}/voice`, { method: "PUT", body: change }),
    members: (id: Uuid) => call<{ items: Member[]; total: number }>(`/servers/${id}/members?limit=100`),
    /** Журнал действий, свежие первыми; следующая страница — `before` = последний id. */
    auditLog: (
      id: Uuid,
      query: { before?: Uuid; action?: string; actorId?: Uuid; targetId?: Uuid; limit?: number } = {},
    ) => {
      const params = new URLSearchParams();
      if (query.before) params.set("before", query.before);
      if (query.action) params.set("action", query.action);
      if (query.actorId) params.set("actor_id", query.actorId);
      if (query.targetId) params.set("target_id", query.targetId);
      params.set("limit", String(query.limit ?? 50));
      return call<{ items: AuditEntry[] }>(`/servers/${id}/audit-log?${params}`);
    },
  },

  /** АвтоМод — запрещённые слова сервера; читать и править — по праву управлять сервером. */
  automod: {
    get: (serverId: Uuid) =>
      call<{ enabled: boolean; words: string[]; mention_limit?: number | null }>(`/servers/${serverId}/automod`),
    /** `mention_limit` — «спам упоминаниями», 2–50; `null` — правило выключено. */
    save: (serverId: Uuid, body: { enabled: boolean; words: string[]; mention_limit: number | null }) =>
      call<{ enabled: boolean; words: string[]; mention_limit?: number | null }>(`/servers/${serverId}/automod`, {
        method: "PUT",
        body,
      }),
  },

  /** Экран приветствия — как в Discord: описание и до пяти рекомендованных каналов. */
  welcome: {
    get: (serverId: Uuid) => call<WelcomeScreen>(`/servers/${serverId}/welcome-screen`),
    save: (serverId: Uuid, body: WelcomeScreen) =>
      call<WelcomeScreen>(`/servers/${serverId}/welcome-screen`, { method: "PUT", body }),
  },

  /** События сервера: ещё не прошедшие, по времени начала. */
  events: {
    list: (serverId: Uuid) => call<{ items: ServerEvent[] }>(`/servers/${serverId}/events`),
    create: (serverId: Uuid, body: ServerEventDraft) =>
      call<ServerEvent>(`/servers/${serverId}/events`, { method: "POST", body }),
    /** Правка целиком — тем же телом, что и создание; отклики остаются. */
    update: (serverId: Uuid, eventId: Uuid, body: ServerEventDraft) =>
      call<ServerEvent>(`/servers/${serverId}/events/${eventId}`, { method: "PUT", body }),
    remove: (serverId: Uuid, eventId: Uuid) =>
      call<void>(`/servers/${serverId}/events/${eventId}`, { method: "DELETE" }),
    interest: (serverId: Uuid, eventId: Uuid, on: boolean) =>
      call<void>(`/servers/${serverId}/events/${eventId}/interest`, { method: on ? "PUT" : "DELETE" }),
    /** «Начать» раньше срока и «Завершить» идущее — как в Discord. Повторяющееся переезжает на следующую дату. */
    start: (serverId: Uuid, eventId: Uuid) =>
      call<void>(`/servers/${serverId}/events/${eventId}/start`, { method: "POST" }),
    end: (serverId: Uuid, eventId: Uuid) =>
      call<void>(`/servers/${serverId}/events/${eventId}/end`, { method: "POST" }),
  },

  channels: {
    create: (
      serverId: Uuid,
      body: {
        name: string;
        channel_type: "text" | "voice" | "stage" | "forum" | "announcement";
        category_id?: Uuid | null;
        topic?: string | null;
      },
    ) => call<Channel>(`/servers/${serverId}/channels`, { method: "POST", body }),
    /*
     * Присланный `null` у темы и категории — не то же самое, что отсутствие
     * поля: первое стирает, второе не трогает. Поэтому тело собирается по
     * месту вызова, а не сводится к одному объекту с необязательными полями.
     */
    update: (id: Uuid, body: Record<string, unknown>) =>
      call<Channel>(`/channels/${id}`, { method: "PATCH", body }),
    remove: (id: Uuid) => call<void>(`/channels/${id}`, { method: "DELETE" }),
  },

  read: {
    /** Непрочитанное по всем видимым каналам сразу. */
    unread: () =>
      call<{
        items: {
          channel_id: Uuid;
          server_id: Uuid | null;
          /** Не больше потолка: выше сотни точное число всё равно не видно. */
          unread: number;
          /** Счёт уткнулся в потолок: `unread` значит «столько и больше». */
          capped: boolean;
          mentions: number;
          /** Сколько веток канала ждут прочтения — из тех, за которыми следим. */
          threads: number;
          last_read_id: Uuid | null;
          /** С какого сообщения начинается непрочитанное. */
          first_unread_id: Uuid | null;
        }[];
      }>("/users/current/unread"),
    mark: (channelId: Uuid, message_id: Uuid) =>
      call<void>(`/channels/${channelId}/read`, { method: "PUT", body: { message_id } }),
    /** «Отметить непрочитанным»: с этого сообщения канал снова непрочитан. Ответ — свежая сводка. */
    unmark: (channelId: Uuid, message_id: Uuid) =>
      call<{
        unread: number;
        capped: boolean;
        mentions: number;
        last_read_id: Uuid | null;
        first_unread_id: Uuid | null;
      }>(`/channels/${channelId}/unread`, { method: "POST", body: { message_id } }),
  },

  notify: {
    /**
     * Что выбрано про оклик. Только выбранное: чего в ответе нет — то «как
     * выше», а не «молчать».
     */
    choices: () =>
      call<{
        /** `until` — заглушён на время, до какого. */
        channels: { channel_id: Uuid; level: NotifyLevel; until?: string | null }[];
        servers: { server_id: Uuid; level: NotifyLevel }[];
        /** Закреплённые вверху каналы — «Закрепить канал вверху», как в Discord. */
        pinned?: Uuid[];
      }>("/users/current/notify"),
    /** `null` — вернуть к умолчанию: у канала это сервер, у сервера — «звать». */
    /** `minutes` — «заглушить на время», только с `none`. */
    setChannel: (channelId: Uuid, level: NotifyLevel | null, minutes: number | null = null) =>
      call<void>(`/channels/${channelId}/notify`, { method: "PUT", body: { level, minutes } }),
    /** «Включить уведомления»: на время заглушённый — к прежнему своему уровню. */
    unmuteChannel: (channelId: Uuid) =>
      call<void>(`/channels/${channelId}/notify`, { method: "PUT", body: { unmute: true } }),
    setServer: (serverId: Uuid, level: NotifyLevel | null) =>
      call<void>(`/servers/${serverId}/notify`, { method: "PUT", body: { level } }),
    pin: (channelId: Uuid) => call<void>(`/channels/${channelId}/pin`, { method: "PUT" }),
    unpin: (channelId: Uuid) => call<void>(`/channels/${channelId}/pin`, { method: "DELETE" }),
  },

  categories: {
    create: (serverId: Uuid, name: string) =>
      call<Category>(`/servers/${serverId}/categories`, { method: "POST", body: { name } }),
    update: (id: Uuid, body: { name?: string; position?: number }) =>
      call<Category>(`/categories/${id}`, { method: "PATCH", body }),
    /** Каналы при этом не пропадают — поднимаются на верхний уровень. */
    remove: (id: Uuid) => call<void>(`/categories/${id}`, { method: "DELETE" }),
  },

  /**
   * Превью ссылки. `undefined` — показывать нечего (204): сайт не ответил, это
   * не страница или адрес закрыт.
   */
  embeds: {
    /**
     * Превью ссылки; `null` — у страницы его нет (сервер ответил 204). Именно
     * `null`, а не пустота: `undefined` react-query за данные не принимает и
     * на каждую такую ссылку пишет в журнал ошибку.
     */
    get: async (url: string) =>
      (await call<LinkPreview | undefined>(`/embeds?url=${encodeURIComponent(url)}`)) ?? null,
  },

  messages: {
    /**
     * Страница ленты.
     *
     * `before`/`after` — от сообщения в одну сторону. `nearby` — вокруг него:
     * половина до, половина после. Им и открывается канал, у которого есть
     * непрочитанное: над чертой нужен кусок уже прочитанного, иначе непонятно,
     * к чему это ответ.
     */
    list: (channelId: Uuid, params: { limit: number; before?: Uuid; after?: Uuid; nearby?: Uuid }) => {
      const query = new URLSearchParams({ limit: String(params.limit) });
      if (params.before) query.set("before", params.before);
      if (params.after) query.set("after", params.after);
      if (params.nearby) query.set("nearby", params.nearby);
      return call<Message[]>(`/channels/${channelId}/messages?${query}`);
    },
    /** Поиск по ленте канала, свежие сверху. Слова — по началу, с русской морфологией. */
    search: (channelId: Uuid, q: string, filter: SearchFilter = {}) =>
      call<Message[]>(`/channels/${channelId}/messages/search?${searchParams(q, filter)}`),
    /** То же по всему серверу — по каналам, где открыта история. */
    searchServer: (serverId: Uuid, q: string, filter: SearchFilter = {}) =>
      call<Message[]>(`/servers/${serverId}/messages/search?${searchParams(q, filter)}`),
    post: (
      channelId: Uuid,
      content: string,
      attachment_ids: Uuid[] = [],
      reply_to_id: Uuid | null = null,
      // Звать ли автора того, на что ответ, — «@ВКЛ» в Discord.
      mention_author = true,
      sticker_id: Uuid | null = null,
      poll: PollDraft | null = null,
      // Тихое — @silent в Discord: получателей не будит.
      silent = false,
      // Заголовок поста — только в форуме, и там он обязателен.
      title: string | null = null,
      // Метки поста — из набора форума, до пяти.
      tag_ids: Uuid[] = [],
      // Какие из вложений — спойлеры: закрыты, пока не нажмут.
      spoiler_ids: Uuid[] = [],
      // Голосовое: единственное вложение — запись, а это её длительность и волна.
      voice: { duration_ms: number; waveform: string } | null = null,
      // Описания вложений — для читалки экрана и подписи по наведению.
      descriptions: { id: Uuid; text: string }[] = [],
    ) =>
      call<Message>(`/channels/${channelId}/messages`, {
        method: "POST",
        body: {
          content,
          attachment_ids,
          reply_to_id,
          mention_author,
          sticker_id,
          poll,
          silent,
          title,
          tag_ids,
          spoiler_ids,
          voice,
          descriptions,
        },
      }),
    /** Посты форума: где что-то было последним — сверху, по 50; с набором меток. */
    /** `sort`: `activity` — где что-то было последним, `created` — новые посты сверху. */
    posts: (channelId: Uuid, offset = 0, sort: "activity" | "created" = "activity") =>
      call<{ items: ForumPost[]; tags: ForumTag[] }>(`/channels/${channelId}/posts?offset=${offset}&sort=${sort}`),
    /** Закрыть или открыть пост (ветку) — по праву управлять сообщениями. */
    lockThread: (channelId: Uuid, threadId: Uuid, locked: boolean) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/lock`, { method: "PUT", body: { locked } }),
    /** Закрепить пост наверху форума или открепить. */
    pinThread: (channelId: Uuid, threadId: Uuid, pinned: boolean) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/lock`, { method: "PUT", body: { pinned } }),
    /** Набор меток форума целиком; пропавшие уходят и с постов. */
    setTags: (channelId: Uuid, names: string[]) =>
      call<{ items: ForumTag[] }>(`/channels/${channelId}/tags`, { method: "PUT", body: { names } }),
    /** Свой голос в опросе целиком; пусто — снять. Ответ — опрос с новым счётом. */
    /** «Завершить опрос» — автору или тому, кто управляет сообщениями. */
    endPoll: (channelId: Uuid, messageId: Uuid) =>
      call<Poll>(`/channels/${channelId}/messages/${messageId}/poll/end`, { method: "POST" }),
    /** Кто голосовал за ответ — до сотни, в порядке голосов. */
    pollVoters: (channelId: Uuid, messageId: Uuid, answerId: Uuid) =>
      call<MessageAuthor[]>(`/channels/${channelId}/messages/${messageId}/poll/answers/${answerId}/voters`),
    vote: (channelId: Uuid, messageId: Uuid, answerIds: Uuid[]) =>
      call<Poll>(`/channels/${channelId}/messages/${messageId}/poll`, {
        method: "PUT",
        body: { answer_ids: answerIds },
      }),
    /** Переслать сообщение в этот канал — снимком, с комментарием или без. */
    forward: (channelId: Uuid, messageId: Uuid, content = "") =>
      call<Message>(`/channels/${channelId}/messages/forward`, {
        method: "POST",
        body: { message_id: messageId, content },
      }),
    /** Правка текста; `removeAttachments` — какие вложения убрать, как крестиком в Discord. */
    edit: (channelId: Uuid, messageId: Uuid, content: string, removeAttachments: Uuid[] = []) =>
      call<Message>(`/channels/${channelId}/messages/${messageId}`, {
        method: "PATCH",
        body: removeAttachments.length ? { content, remove_attachments: removeAttachments } : { content },
      }),
    remove: (channelId: Uuid, messageId: Uuid) =>
      call<void>(`/channels/${channelId}/messages/${messageId}`, { method: "DELETE" }),
    /** Скрыть превью ссылок — автору или модератору; вернуть нельзя. */
    suppressEmbeds: (channelId: Uuid, messageId: Uuid) =>
      call<void>(`/channels/${channelId}/messages/${messageId}/embeds`, { method: "DELETE" }),
    /*
     * Значок стоит в адресе, поэтому кодируется: `👍` уезжает как
     * `%F0%9F%91%8D`. Поставить и снять — `PUT` и `DELETE` одного ресурса, оба
     * повторяются без последствий, и клиенту не приходится гадать, в каком он
     * состоянии.
     */
    /** Кто поставил значок — как «Реакции» в Discord, до сотни, в порядке, в каком ставили. */
    reactors: (channelId: Uuid, messageId: Uuid, emoji: string) =>
      call<MessageAuthor[]>(
        `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
      ),
    react: (channelId: Uuid, messageId: Uuid, emoji: string) =>
      call<void>(
        `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
        { method: "PUT" },
      ),
    unreact: (channelId: Uuid, messageId: Uuid, emoji: string) =>
      call<void>(
        `/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
        { method: "DELETE" },
      ),

    /** Закреплённые в канале, последние закреплённые сверху. */
    pins: (channelId: Uuid) => call<Message[]>(`/channels/${channelId}/pins`),
    /** Закреплённые в ветке — у ветки они свои, в закреплённых канала их нет. */
    threadPins: (channelId: Uuid, threadId: Uuid) =>
      call<Message[]>(`/channels/${channelId}/threads/${threadId}/pins`),
    /** Закрепить и открепить — `PUT` и `DELETE` одного ресурса, повтор без последствий. */
    pin: (channelId: Uuid, messageId: Uuid) =>
      call<void>(`/channels/${channelId}/pins/${messageId}`, { method: "PUT" }),
    unpin: (channelId: Uuid, messageId: Uuid) =>
      call<void>(`/channels/${channelId}/pins/${messageId}`, { method: "DELETE" }),
    /** Разослать объявление подписчикам канала. Второй раз — `already_published`. */
    publish: (channelId: Uuid, messageId: Uuid) =>
      call<Message>(`/channels/${channelId}/messages/${messageId}/publish`, { method: "POST" }),

    /**
     * «Печатаю». Ответа нет и не нужно.
     *
     * Частоту держит тот, кто зовёт: сервер ничего не запоминает, а каждое
     * событие продлевает надпись у остальных ещё на несколько секунд.
     */
    typing: (channelId: Uuid) =>
      call<void>(`/channels/${channelId}/typing`, { method: "POST" }),
  },

  /*
   * Ветки живут под каналом: своих прав у них нет, право на них — право
   * канала. Ответ по корню заводит ветку, если её ещё не было; по
   * идентификатору ветки — когда корень уже удалён.
   */
  threads: {
    /** «Скрыть после бездействия»: минут тишины до «затихших» — 60, 1440, 4320 или 10080. */
    quiet: (channelId: Uuid, threadId: Uuid, minutes: number) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/quiet`, { method: "PUT", body: { minutes } }),
    /** «Переименовать ветку»: пусто — снять имя (у поста форума нельзя). */
    title: (channelId: Uuid, threadId: Uuid, title: string | null) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/title`, { method: "PUT", body: { title } }),
    /** Подписанные ветки сервера, пока не затихли, — под своими каналами в списке, как в Discord. */
    followed: (serverId: Uuid) =>
      call<{ items: FollowedThread[] }>(`/servers/${serverId}/threads/followed`),
    list: (channelId: Uuid, followed: boolean) =>
      call<{ items: ThreadListItem[] }>(
        `/channels/${channelId}/threads?followed=${followed}&limit=100`,
      ),
    get: (channelId: Uuid, threadId: Uuid) =>
      call<Thread>(`/channels/${channelId}/threads/${threadId}`),
    messages: (channelId: Uuid, threadId: Uuid, params: { limit: number; before?: Uuid }) => {
      const query = new URLSearchParams({ limit: String(params.limit) });
      if (params.before) query.set("before", params.before);
      return call<Message[]>(`/channels/${channelId}/threads/${threadId}/messages?${query}`);
    },
    /* Тело ответа то же, что у сообщения в канале: сервер разбирает его одним обработчиком. */
    replyToRoot: (
      channelId: Uuid,
      rootId: Uuid,
      content: string,
      attachment_ids: Uuid[] = [],
      extra: ThreadReplyExtra = {},
    ) =>
      call<Message>(`/channels/${channelId}/messages/${rootId}/thread`, {
        method: "POST",
        body: { content, attachment_ids, reply_to_id: null, ...extra },
      }),
    reply: (
      channelId: Uuid,
      threadId: Uuid,
      content: string,
      attachment_ids: Uuid[] = [],
      extra: ThreadReplyExtra = {},
    ) =>
      call<Message>(`/channels/${channelId}/threads/${threadId}/messages`, {
        method: "POST",
        body: { content, attachment_ids, reply_to_id: null, ...extra },
      }),
    follow: (channelId: Uuid, threadId: Uuid, on: boolean) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/follow`, {
        method: on ? "PUT" : "DELETE",
      }),
    /** Уровень ветки; `minutes` — «Заглушить ветку» на срок (только с `none`), `unmute` — снять заглушение. */
    setNotify: (
      channelId: Uuid,
      threadId: Uuid,
      level: NotifyLevel | null,
      options: { minutes?: number; unmute?: boolean } = {},
    ) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/notify`, {
        method: "PUT",
        body: { level, ...options },
      }),
    /** «Закрыть ветку» — как в Discord: уходит из списков, ответ открывает её снова. */
    close: (channelId: Uuid, threadId: Uuid, closed: boolean) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/close`, { method: "PUT", body: { closed } }),
    /** «Удалить ветку»: ответы уходят вместе с ней, корень остаётся в канале. */
    remove: (channelId: Uuid, threadId: Uuid) =>
      call<void>(`/channels/${channelId}/threads/${threadId}`, { method: "DELETE" }),
    /** Медленный режим ветки, секунд; 0 — снять. Свой, а не канала. */
    slowmode: (channelId: Uuid, threadId: Uuid, seconds: number) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/slowmode`, { method: "PUT", body: { seconds } }),
    /** Кто в ветке — подписанные, как список участников ветки в Discord. */
    members: (channelId: Uuid, threadId: Uuid) =>
      call<{ items: PublicUser[] }>(`/channels/${channelId}/threads/${threadId}/members`),
    read: (channelId: Uuid, threadId: Uuid, message_id: Uuid) =>
      call<void>(`/channels/${channelId}/threads/${threadId}/read`, {
        method: "PUT",
        body: { message_id },
      }),
  },

  directs: {
    list: () => call<{ items: DirectChannel[] }>("/directs"),
    open: (userId: Uuid) => call<DirectChannel>(`/directs/${userId}`, { method: "POST" }),
    /** Закрыть переписку двоих у себя — новое сообщение вернёт её в список. */
    close: (channelId: Uuid) => call<void>(`/directs/${channelId}`, { method: "DELETE" }),
    /** Новая группа из друзей: вместе с создателем — не больше 10. */
    createGroup: (userIds: Uuid[], name?: string | null) =>
      call<DirectChannel>("/directs/groups", { method: "POST", body: { user_ids: userIds, name: name ?? null } }),
    renameGroup: (channelId: Uuid, name: string | null) =>
      call<DirectChannel>(`/directs/groups/${channelId}`, { method: "PATCH", body: { name } }),
    /** Значок группы — файлом с назначением `icon`; `null` — снять. */
    setGroupIcon: (channelId: Uuid, fileId: Uuid | null) =>
      call<DirectChannel>(`/directs/groups/${channelId}/icon`, { method: "PUT", body: { file_id: fileId } }),
    addToGroup: (channelId: Uuid, userId: Uuid) =>
      call<DirectChannel>(`/directs/groups/${channelId}/recipients/${userId}`, { method: "PUT" }),
    /** Свой номер — выйти, чужой — исключить (только владельцу). */
    removeFromGroup: (channelId: Uuid, userId: Uuid) =>
      call<void>(`/directs/groups/${channelId}/recipients/${userId}`, { method: "DELETE" }),
  },

  invites: {
    list: (serverId: Uuid) => call<{ items: Invite[] }>(`/servers/${serverId}/invites`),
    create: (
      serverId: Uuid,
      body: { max_uses: number | null; expires_in_hours: number | null; channel_id?: Uuid | null },
    ) =>
      call<Invite>(`/servers/${serverId}/invites`, { method: "POST", body }),
    use: (code: string) =>
      call<{ server_id: Uuid; joined: boolean; channel_id?: Uuid | null }>(`/invites/${code}`, { method: "POST" }),
    remove: (code: string) => call<void>(`/invites/${code}`, { method: "DELETE" }),
    /** Что за сервер за кодом — для карточки приглашения в ленте. */
    preview: (code: string) =>
      call<{
        code: string;
        server_id: Uuid;
        name: string;
        description: string | null;
        icon_id: Uuid | null;
        members: number;
        online: number;
        channel_id: Uuid | null;
        joined: boolean;
        banner: string | null;
        /** Приглашения на сервер приостановлены. */
        paused?: boolean;
        /** Канал приглашения — у голосового карточка зовёт в голос, как в Discord. */
        channel_name?: string | null;
        channel_type?: ChannelType | null;
      }>(`/invites/${code}`),
  },

  /** Звуковая панель сервера. Играется звук в звонке, а слышат все в нём. */
  sounds: {
    list: (serverId: Uuid) => call<{ items: ServerSound[] }>(`/servers/${serverId}/sounds`),
    create: (serverId: Uuid, body: { name: string; file_id: Uuid; emoji?: string | null; volume?: number }) =>
      call<ServerSound>(`/servers/${serverId}/sounds`, { method: "POST", body }),
    update: (serverId: Uuid, soundId: Uuid, body: { name?: string; emoji?: string | null; volume?: number }) =>
      call<ServerSound>(`/servers/${serverId}/sounds/${soundId}`, { method: "PATCH", body }),
    remove: (serverId: Uuid, soundId: Uuid) =>
      call<void>(`/servers/${serverId}/sounds/${soundId}`, { method: "DELETE" }),
    play: (channelId: Uuid, soundId: Uuid) =>
      call<void>(`/channels/${channelId}/voice/sound`, { method: "POST", body: { sound_id: soundId } }),
  },

  bans: {
    list: (serverId: Uuid) => call<{ items: Ban[] }>(`/servers/${serverId}/bans`),
    ban: (
      serverId: Uuid,
      userId: Uuid,
      body: { reason?: string; expires_in_days?: number | null; delete_message_seconds?: number },
    ) =>
      call<Ban>(`/servers/${serverId}/bans/${userId}`, { method: "PUT", body }),
    unban: (serverId: Uuid, userId: Uuid) =>
      call<void>(`/servers/${serverId}/bans/${userId}`, { method: "DELETE" }),
    /** Тайм-аут на столько минут — как в Discord: читать можно, писать и реагировать нельзя. */
    timeout: (serverId: Uuid, userId: Uuid, minutes: number) =>
      call<void>(`/servers/${serverId}/members/${userId}/timeout`, { method: "PUT", body: { minutes } }),
    untimeout: (serverId: Uuid, userId: Uuid) =>
      call<void>(`/servers/${serverId}/members/${userId}/timeout`, { method: "DELETE" }),
  },

  roles: {
    list: (serverId: Uuid) => call<{ items: Role[] }>(`/servers/${serverId}/roles`),
    create: (serverId: Uuid, body: { name: string; rank: number; permissions?: Override }) =>
      call<Role>(`/servers/${serverId}/roles`, { method: "POST", body }),
    update: (serverId: Uuid, roleId: Uuid, body: Record<string, unknown>) =>
      call<Role>(`/servers/${serverId}/roles/${roleId}`, { method: "PATCH", body }),
    remove: (serverId: Uuid, roleId: Uuid) =>
      call<void>(`/servers/${serverId}/roles/${roleId}`, { method: "DELETE" }),
    assign: (serverId: Uuid, userId: Uuid, roleId: Uuid) =>
      call<void>(`/servers/${serverId}/members/${userId}/roles/${roleId}`, { method: "PUT" }),
    unassign: (serverId: Uuid, userId: Uuid, roleId: Uuid) =>
      call<void>(`/servers/${serverId}/members/${userId}/roles/${roleId}`, { method: "DELETE" }),
    /**
     * Что уже записано на канале поверх ролей сервера.
     *
     * Нужно, чтобы редактор открывался с тем, что там есть: без этого правка
     * начиналась с чистого листа и сохранением стирала соседние записи.
     */
    channelOverrides: (channelId: Uuid) =>
      call<{
        default: Override;
        roles: { role_id: Uuid; permissions: Override }[];
        synced?: boolean;
        /** Права на отдельных участников — «Добавить участников» в Discord. */
        members?: { user_id: Uuid; permissions: Override }[];
      }>(`/channels/${channelId}/permissions/overrides`),
    setChannelDefault: (channelId: Uuid, permissions: Override) =>
      call<void>(`/channels/${channelId}/permissions/default`, { method: "PUT", body: { permissions } }),
    setChannelRole: (channelId: Uuid, roleId: Uuid, permissions: Override) =>
      call<void>(`/channels/${channelId}/permissions/${roleId}`, { method: "PUT", body: { permissions } }),
    /** Права канала на одного участника; пустая пара снимает запись. */
    setChannelMember: (channelId: Uuid, userId: Uuid, permissions: Override) =>
      call<void>(`/channels/${channelId}/permissions/users/${userId}`, { method: "PUT", body: { permissions } }),
    /**
     * «Просмотр сервера от лица роли»: права на сервере и каналы, которые видит
     * участник только с этими ролями. Пусто — только роль по умолчанию.
     */
    preview: (serverId: Uuid, roleIds: Uuid[]) =>
      call<{ permissions: number; channel_ids: Uuid[] }>(
        `/servers/${serverId}/roles/preview${roleIds.length ? `?${roleIds.map((id) => `role_id=${id}`).join("&")}` : ""}`,
      ),
    /** «Синхронизировать с категорией»: права канала — копией прав категории. */
    syncChannel: (channelId: Uuid) => call<void>(`/channels/${channelId}/permissions/sync`, { method: "POST" }),
    /** Права категории — их повторяют синхронизированные с ней каналы. */
    categoryOverrides: (categoryId: Uuid) =>
      call<{
        default: Override;
        roles: { role_id: Uuid; permissions: Override }[];
        synced?: boolean;
        /** У категории записей на участников нет: поле — ради общего вида с каналом. */
        members?: { user_id: Uuid; permissions: Override }[];
      }>(`/categories/${categoryId}/permissions/overrides`),
    setCategoryDefault: (categoryId: Uuid, permissions: Override) =>
      call<void>(`/categories/${categoryId}/permissions/default`, { method: "PUT", body: { permissions } }),
    setCategoryRole: (categoryId: Uuid, roleId: Uuid, permissions: Override) =>
      call<void>(`/categories/${categoryId}/permissions/${roleId}`, { method: "PUT", body: { permissions } }),
  },

  permissions: {
    /** Что можно на сервере: итог наложения плюс старшинство. */
    onServer: (serverId: Uuid) =>
      call<{
        server_id: Uuid;
        permissions: number;
        rank: number;
        is_owner: boolean;
        rules_pending?: boolean;
        /** До какого времени новичок только читает — уровень проверки сервера. */
        verify_until?: string | null;
      }>(`/servers/${serverId}/permissions/current`),
    /** Что можно в канале. `server_id: null` — личная переписка. */
    onChannel: (channelId: Uuid) =>
      call<{ channel_id: Uuid; server_id: Uuid | null; permissions: number }>(
        `/channels/${channelId}/permissions/current`,
      ),
  },

  emoji: {
    /**
     * Чем этот человек чаще всего отвечает — за последние полгода.
     *
     * Спрашивается раз за сеанс: из этого собирается ряд быстрых реакций.
     * Считает сервер по самим реакциям, поэтому счёт переживает перезаход и
     * едет вместе с человеком на другое устройство.
     */
    frequent: () => call<{ items: { emoji: string; uses: number }[] }>("/users/current/emoji"),
    /** Избранные знаки — свой список наверху панели, общий для устройств. */
    favorites: () => call<{ items: string[] }>("/users/current/emoji/favorites"),
    saveFavorites: (items: string[]) =>
      call<{ items: string[] }>("/users/current/emoji/favorites", { method: "PUT", body: { items } }),
    /** Свои знаки сервера — как эмодзи сервера в Discord. Сами знаки едут и в состоянии сервера. */
    list: (serverId: Uuid) => call<{ items: ServerEmoji[] }>(`/servers/${serverId}/emoji`),
    create: (serverId: Uuid, body: { name: string; file_id: Uuid }) =>
      call<ServerEmoji>(`/servers/${serverId}/emoji`, { method: "POST", body }),
    /** Стикеры сервера: список едет в состоянии сервера, здесь — добавить и удалить. */
    createSticker: (serverId: Uuid, body: { name: string; file_id: Uuid }) =>
      call<ServerSticker>(`/servers/${serverId}/stickers`, { method: "POST", body }),
    removeSticker: (serverId: Uuid, stickerId: Uuid) =>
      call<void>(`/servers/${serverId}/stickers/${stickerId}`, { method: "DELETE" }),
    rename: (serverId: Uuid, emojiId: Uuid, name: string) =>
      call<ServerEmoji>(`/servers/${serverId}/emoji/${emojiId}`, { method: "PATCH", body: { name } }),
    remove: (serverId: Uuid, emojiId: Uuid) =>
      call<void>(`/servers/${serverId}/emoji/${emojiId}`, { method: "DELETE" }),
  },

  voice: {
    /** Статус канала — «чем заняты»; `null` — снять. Задаёт сидящий в звонке. */
    setStatus: (channelId: Uuid, status: string | null) =>
      call<void>(`/channels/${channelId}/voice/status`, { method: "PUT", body: { status } }),
    /** `ringing` — кого вызывают; бывает только у переписки. */
    participants: (channelId: Uuid) =>
      call<{ items: VoiceParticipant[]; ringing?: CallRing[] }>(`/channels/${channelId}/voice`),
    /** «Позвать в звонок» — тех из переписки, кого в звонке нет; зовёт тот, кто в звонке. */
    ring: (channelId: Uuid, userIds: Uuid[] | null = null) =>
      call<void>(`/channels/${channelId}/voice/ring`, { method: "POST", body: { user_ids: userIds } }),
    /** Отклонить вызов — у себя перестаёт звонить. */
    decline: (channelId: Uuid) => call<void>(`/channels/${channelId}/voice/decline`, { method: "POST" }),
    join: (channelId: Uuid) => call<VoiceGrant>(`/channels/${channelId}/voice/join`, { method: "POST" }),
    /** Поднять или опустить руку на сцене — попросить слова. */
    stageHand: (channelId: Uuid, raised: boolean) =>
      call<void>(`/channels/${channelId}/voice/stage/hand`, { method: "PUT", body: { raised } }),
    /** Позвать выступать или вернуть в слушатели; себя вернуть может каждый. */
    stageSpeaker: (channelId: Uuid, userId: Uuid, speaker: boolean) =>
      call<void>(`/channels/${channelId}/voice/stage/speakers/${userId}`, { method: "PUT", body: { speaker } }),
    /**
     * `keepalive` — для выхода при закрытии страницы: обычный запрос браузер
     * обрывает вместе со вкладкой, и звонок остался бы с призраком.
     */
    leave: (channelId: Uuid, keepalive = false, signal?: AbortSignal) =>
      call<void>(`/channels/${channelId}/voice/leave`, {
        method: "POST",
        keepalive,
        signal,
        refreshOnUnauthorized: signal === undefined,
      }),

    /**
     * Своё состояние в звонке — микрофон, звук, показ.
     *
     * Целиком, а не по одному признаку: клиент знает все три и шлёт их
     * вместе, а разбирать «что именно переключили» значило бы заводить
     * второй ответ на тот же вопрос.
     */
    state: (channelId: Uuid, flags: { microphone: boolean; deafened: boolean; live: boolean }) =>
      call<void>(`/channels/${channelId}/voice/state`, { method: "POST", body: flags }),

    /** Отключить от звонка — по праву перемещать участников. */
    disconnect: (channelId: Uuid, userId: Uuid) =>
      call<void>(`/channels/${channelId}/voice/${userId}/disconnect`, { method: "POST" }),
    /** Перенести в другой голосовой канал: переходит его клиент, по событию. */
    move: (channelId: Uuid, userId: Uuid, to: Uuid) =>
      call<void>(`/channels/${channelId}/voice/${userId}/move`, { method: "POST", body: { channel_id: to } }),

    /*
     * Точка приёма готового потока: нужна нативному захвату, который
     * кодирует сам и отдаёт по WHIP, минуя браузер.
     *
     * Право то же, что даёт обычную демонстрацию. Разница только в том, кто
     * кодирует.
     */
    openIngress: (channelId: Uuid) =>
      call<Ingress>(`/channels/${channelId}/voice/ingress`, { method: "POST" }),
    closeIngress: (channelId: Uuid, signal?: AbortSignal) =>
      call<void>(`/channels/${channelId}/voice/ingress`, {
        method: "DELETE",
        signal,
        refreshOnUnauthorized: signal === undefined,
      }),
  },

  files: {
    /**
     * `onProgress` необязателен: аватару и значку полоска не нужна — они
     * мелкие и уезжают мгновенно. Нужна она вложению, и там она есть.
     */
    upload: (
      file: File,
      tag: "attachment" | "avatar" | "icon" | "sound" | "emoji" | "sticker" | "banner" | "decoration",
      onProgress?: (sent: number, total: number) => void,
    ) => {
      const form = new FormData();
      form.append("file", file);
      return upload<FileMeta>(`/files?tag=${tag}`, form, onProgress);
    },
  },
};
